import React, { useState, useRef, useEffect, useMemo } from "react";
import { ChevronDown } from "lucide-react";
import { countryCodes } from "../data/CountryCodes";
import { Input } from "./ui/input";

// Popular countries to show first (by usage/population)
const popularCountryCodes = [
  "27", "1", "44", "33", "49", "81", "86", "91", "55", "61",
  "7", "39", "34", "52", "82", "65", "31", "46", "41", "47",
];

interface CustomColors {
  backgroundColor?: string;
  textColor?: string;
  inputBackgroundColor?: string;
}

// Unicode regional-indicator flag from a 2-letter ISO code ("za" → 🇿🇦).
// Replaces the flag-icons CSS package, which inlined ~500 SVG flags (megabytes)
// into the widget bundle just for this dropdown.
const flagEmoji = (iso2: string): string =>
  iso2
    .toUpperCase()
    .replace(/[A-Z]/g, (c) => String.fromCodePoint(c.charCodeAt(0) + 127397));

interface CountryCodeDropdownProps {
  value: string;
  onChange: (code: string) => void;
  className?: string;
  inputClassName?: string;
  inputStyles?: React.CSSProperties;
  customColors?: CustomColors;
  /** Render the trigger borderless so it can sit inside a joined control
   *  (the parent draws the ring/background). */
  embedded?: boolean;
}

/**
 * Custom country code dropdown that works inside Shadow DOM without
 * affecting the host page. Unlike Radix UI Select, this component:
 * - Does NOT lock body scroll (no overflow:hidden on document.body)
 * - Does NOT trap focus outside the Shadow DOM
 * - Positions the dropdown within the widget panel
 */
export function CountryCodeDropdown({
  value,
  onChange,
  className = "",
  inputClassName = "",
  inputStyles = {},
  customColors = {},
  embedded = false,
}: CountryCodeDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  // Track selected country by unique iso2 code to distinguish countries
  // that share the same dial code (e.g. Canada "ca" vs USA "us" both have code "1")
  const [selectedIso2, setSelectedIso2] = useState<string>(() => {
    const match = countryCodes.find((c) => c.code === value);
    return match?.iso2 || "";
  });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Close on outside click (within Shadow DOM)
  useEffect(() => {
    if (!isOpen) return;

    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        setIsOpen(false);
        setSearch("");
      }
    };

    // Listen on the shadow root or document
    const root = dropdownRef.current?.getRootNode() as ShadowRoot | Document;
    root?.addEventListener("mousedown", handleClick);
    return () => root?.removeEventListener("mousedown", handleClick);
  }, [isOpen]);

  // Focus search input when opened
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      // Small delay to ensure DOM is ready
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsOpen(false);
        setSearch("");
        triggerRef.current?.focus();
      }
    };

    const root = dropdownRef.current?.getRootNode() as ShadowRoot | Document;
    root?.addEventListener("keydown", handleKeyDown);
    return () => root?.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  // Filtered and sorted countries
  const filteredCountries = useMemo(() => {
    let filtered = countryCodes;

    if (debouncedSearch) {
      const s = debouncedSearch.toLowerCase();
      filtered = countryCodes.filter(
        (c) => c.name.toLowerCase().includes(s) || c.code.includes(s)
      );
    }

    const popular = filtered.filter((c) => popularCountryCodes.includes(c.code));
    const others = filtered.filter((c) => !popularCountryCodes.includes(c.code));

    popular.sort(
      (a, b) =>
        popularCountryCodes.indexOf(a.code) - popularCountryCodes.indexOf(b.code)
    );
    others.sort((a, b) => a.name.localeCompare(b.name));

    return [...popular, ...others].slice(0, 50);
  }, [debouncedSearch]);

  // Look up country by unique iso2 first, fall back to dial code
  const currentCountry = selectedIso2
    ? countryCodes.find((c) => c.iso2 === selectedIso2)
    : countryCodes.find((c) => c.code === value);

  const handleSelect = (country: { code: string; iso2: string }) => {
    setSelectedIso2(country.iso2);
    onChange(country.code);
    setIsOpen(false);
    setSearch("");
  };

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      {/* Trigger Button */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={
          embedded
            ? `flex h-11 flex-shrink-0 items-center gap-1 rounded-l-xl bg-transparent pl-3 pr-2 text-sm focus:outline-none ${inputClassName}`
            : `flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${inputClassName}`
        }
        style={inputStyles}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        {currentCountry ? (
          <span className="flex items-center gap-2">
            <span className="flex-shrink-0 text-base leading-none">{flagEmoji(currentCountry.iso2)}</span>
            <span className={embedded ? "text-[15px] tabular-nums" : "font-mono text-xs"}>
              {embedded ? "+" : ""}{currentCountry.code}
            </span>
          </span>
        ) : (
          <span className="text-muted-foreground text-xs">Code</span>
        )}
        <ChevronDown className={`h-4 w-4 flex-shrink-0 ${embedded ? "ml-0.5 opacity-40" : "ml-1 opacity-50"}`} />
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div
          className="absolute left-0 bottom-full mb-2 w-72 max-h-56 bg-popover rounded-xl ring-1 ring-black/[0.08] shadow-[0_8px_24px_-8px_rgba(16,24,40,0.3),0_2px_6px_rgba(16,24,40,0.08)] overflow-hidden z-[999999] flex flex-col"
          role="listbox"
        >
          {/* Search */}
          <div className="p-2 border-b border-black/[0.06] bg-background flex-shrink-0">
            <Input
              ref={searchInputRef}
              placeholder="Search country..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-9 rounded-lg border-0 bg-black/[0.04] text-sm ring-1 ring-black/[0.06] ring-offset-0 placeholder:text-gray-400 focus-visible:ring-2 focus-visible:ring-offset-0 focus-visible:ring-[color:var(--uif-accent,#2563eb)]"
              // Prevent the click from closing the dropdown
              onMouseDown={(e) => e.stopPropagation()}
            />
          </div>

          {/* Country List */}
          <div className="overflow-y-auto flex-1">
            {filteredCountries.length === 0 ? (
              <div className="p-2 text-sm text-muted-foreground text-center">
                No countries found
              </div>
            ) : (
              filteredCountries.map((country, index) => (
                <button
                  key={`${country.iso2}-${index}`}
                  type="button"
                  role="option"
                  aria-selected={country.iso2 === selectedIso2}
                  className={`flex items-center gap-3 w-full px-3 py-2 text-sm cursor-pointer hover:bg-accent hover:text-accent-foreground transition-colors ${
                    country.iso2 === selectedIso2 ? "bg-accent text-accent-foreground" : ""
                  }`}
                  onClick={() => handleSelect(country)}
                >
                  <span className="flex-shrink-0 text-base leading-none">{flagEmoji(country.iso2)}</span>
                  <span className="flex-1 text-left">{country.name}</span>
                  <span className="font-mono text-xs text-muted-foreground">
                    +{country.code}
                  </span>
                </button>
              ))
            )}
            {search && filteredCountries.length >= 50 && (
              <div className="p-2 text-xs text-muted-foreground text-center border-t">
                Showing first 50 results. Refine search for more.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
